import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut as amplifySignOut,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";

import { identifyUser, resetUser } from "../analytics";
import "./config"; // Amplify.configure() side-effect import
import { AuthContext, type AuthValue, type Tier } from "./useAuth";

/**
 * AuthContext — Task 4.0a.
 *
 * Single source of truth for the user's authentication state in the SPA.
 * Wraps Amplify's auth surface so the rest of the app uses one stable
 * `useAuth()` shape regardless of Amplify version churn.
 *
 *   { user, tier, idToken, loading, signIn, signOut, error }
 *
 * Token refresh:
 *   `fetchAuthSession()` from Amplify returns a freshly-rotated ID token
 *   when the cached one is within 5 minutes of expiry. We call it lazily
 *   via `getIdToken()` (api/client.ts) so every API request gets a valid
 *   token without us needing a refresh timer.
 *
 * Tier extraction:
 *   The ID token's `custom:tier` claim is the source of truth on the
 *   client. The /user/tier polling endpoint (Task 6.12) is for detecting
 *   the post-Stripe upgrade *before* the JWT refreshes — it's not the
 *   primary tier source.
 */

function decodeJwtPayload<T>(token: string): T | null {
  // Lightweight JWT-payload decoder. We trust Amplify's signature
  // verification (it fetched the token from Cognito); this is just for
  // reading the `custom:tier` claim out of the payload. Don't use this
  // for security decisions on its own.
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

interface JwtPayload {
  sub: string;
  email?: string;
  "custom:tier"?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthValue["user"]>(null);
  const [tier, setTier] = useState<Tier>("free");
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthValue["error"]>(null);

  const readSession = useCallback(async (forceRefresh = false) => {
    try {
      // forceRefresh exchanges the refresh token for a NEW id token — needed
      // right after a Stripe upgrade so the `custom:tier` claim flips to paid
      // immediately (the cached token still says free until it expires).
      const session = await fetchAuthSession({ forceRefresh });
      const token = session.tokens?.idToken?.toString() ?? null;
      if (!token) {
        setUser(null);
        setTier("free");
        setIdToken(null);
        return;
      }
      const payload = decodeJwtPayload<JwtPayload>(token);
      // getCurrentUser fills in sub/username — keep its result as the
      // canonical user shape; the JWT payload is the tier source.
      const current = await getCurrentUser().catch(() => null);
      const sub = current?.userId ?? payload?.sub ?? "";
      const email = payload?.email ?? "";
      const resolvedTier: Tier =
        payload?.["custom:tier"] === "paid" ? "paid" : "free";
      setUser({ sub, email });
      setTier(resolvedTier);
      setIdToken(token);
      setError(null);
      // Join analytics to the Cognito identity (distinct ID = sub).
      identifyUser(sub, email, resolvedTier);
    } catch {
      // Not signed in is the normal "no session" path — not an error.
      setUser(null);
      setTier("free");
      setIdToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Listen to Amplify's auth events so the context refreshes when the
  // OAuth callback completes (the AuthCallback page triggers this via
  // signInWithRedirect → Hub dispatches 'signedIn').
  useEffect(() => {
    void readSession();
    const unsub = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "tokenRefresh":
          void readSession();
          break;
        case "signedOut":
          setUser(null);
          setTier("free");
          setIdToken(null);
          // Explicit sign-out only — resetUser() on the anonymous
          // readSession path would churn anonymous IDs every page load.
          resetUser();
          break;
        case "signInWithRedirect_failure":
          setError({
            code: "sso_failed",
            message: "Sign-in failed. Please try again.",
          });
          setLoading(false);
          break;
      }
    });
    return () => {
      unsub();
    };
  }, [readSession]);

  const signIn = useCallback(async () => {
    setError(null);
    try {
      // No `provider` → land on the Managed Login chooser (Google +
      // email/password), rather than jumping straight to Google (Task 1.1a).
      await signInWithRedirect();
    } catch (e) {
      const err = e as Error;
      setError({ code: "sso_redirect_failed", message: err.message });
    }
  }, []);

  const signOutFn = useCallback(async () => {
    try {
      await amplifySignOut();
    } catch (e) {
      const err = e as Error;
      setError({ code: "sign_out_failed", message: err.message });
    }
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      tier,
      idToken,
      loading,
      error,
      signIn,
      signOut: signOutFn,
      refresh: readSession,
    }),
    [user, tier, idToken, loading, error, signIn, signOutFn, readSession],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
